// Port of mobile src/services/functionsService.ts — same callable names,
// payload shapes and error-marker mapping.
import {httpsCallable} from 'firebase/functions';
import {functions} from '../firebase';
import type {Flashcard, QuizQuestion} from '../types/Note';

export class FunctionsError extends Error {
  code: string;
  constructor(code: string, message: string) {
    super(message);
    this.code = code;
  }
}

// resource-exhausted callables surface as a plain marker string in the
// error message (see functions/src/lib/budget.ts and lib/entitlement.ts) —
// map those onto a stable FunctionsError.code so screens can branch on it
// without string-matching the message themselves.
const RESOURCE_EXHAUSTED_MARKERS = [
  'FREE_LIMIT_REACHED',
  'AI_BUDGET_EXCEEDED',
  'VOICE_MESSAGE_LIMIT_REACHED',
];

const call = async <TResult>(
  name: string,
  data: Record<string, unknown>,
): Promise<TResult> => {
  try {
    const result = await httpsCallable(functions, name)(data);
    return result.data as TResult;
  } catch (e) {
    // JS SDK codes come prefixed ("functions/internal") — normalize off.
    const rawCode = (e as {code?: string}).code ?? 'internal';
    const code = rawCode.replace(/^functions\//, '');
    const message = e instanceof Error ? e.message : String(e);
    console.warn(`callable ${name} failed [${code}]: ${message}`);
    const marker = RESOURCE_EXHAUSTED_MARKERS.find(m => message.includes(m));
    if (marker) {
      throw new FunctionsError(marker, message);
    }
    throw new FunctionsError(code, message);
  }
};

export const createYoutubeNote = (
  videoId: string,
  language: string | null,
  summaryLanguage?: string | null,
): Promise<{noteId: string}> =>
  call('createYoutubeNote', {videoId, language, summaryLanguage});

export const translateNote = (
  noteId: string,
  targetLanguage: string,
): Promise<{content: string; language: string}> =>
  call('translateNote', {noteId, targetLanguage});

export const generateQuiz = (
  noteId: string,
): Promise<{questions: QuizQuestion[]}> => call('generateQuiz', {noteId});

export const generateFlashcards = (
  noteId: string,
): Promise<{cards: Flashcard[]}> => call('generateFlashcards', {noteId});

export const assistantChat = (
  noteId: string | null,
  question: string,
): Promise<{answer: string}> => call('assistantChat', {noteId, question});

export const transcribeVoiceMessage = (
  audioBase64: string,
  language?: string | null,
): Promise<{text: string}> =>
  call('transcribeVoiceMessage', {audio: audioBase64, language});

export const summarizeNote = (
  noteId: string,
  summaryLanguage?: string | null,
): Promise<{title: string; summarize: string; emoji: string}> =>
  call('summarizeNote', {noteId, summaryLanguage});

export const retryNote = (
  noteId: string,
  language?: string,
): Promise<{ok: boolean}> => call('retryNote', {noteId, language});

export const deleteAccount = (): Promise<{ok: boolean}> =>
  call('deleteAccount', {});

export const mergeAccounts = (
  sourceIdToken: string,
): Promise<{mergedNotes: number}> => call('mergeAccounts', {sourceIdToken});

export const shareNote = (
  noteId: string,
): Promise<{shareId: string; url: string}> => call('shareNote', {noteId});

export const unshareNote = (noteId: string): Promise<{ok: boolean}> =>
  call('unshareNote', {noteId});
